/**
 * The front page: the best of the last 24 hours, set for this hour of the day.
 *
 * Not a round. The reader's rounds are ten at a time and personal; this is one page for
 * everyone, set from everything that landed in the last day and set again as the day moves.
 * Twelve stories, no more: the lead with its photograph, the five beside it that the country
 * is reading, six more below with their pictures, and the paper's corner (the game, the
 * quiz, the bright side). The wire runs under the masthead with whatever landed last.
 *
 * Honest by construction: stories the reader has opened are marked, never hidden; new
 * stories that land while the page is open are counted, never swapped in under the reader's
 * eyes. Opening a story closes the page and puts the reader in the ordinary card flow, with
 * the next unread stories from this page lined up behind it.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Bell, BellRing, Check, Flame, ImageDown, Pin, Play, RefreshCw, Share2, Sun, X } from "lucide-react";
import type { InSnipCard } from "../types";
import type { EditionPaper } from "../sessionApi";
import { resolveImageUrl } from "../assets";
import { displayCaption } from "../caption";
import { agoShort, dayArc, editionSlot, paperStats, setAtLabel, sourceName } from "./editionTime";
import { Pillars, Sections, scrollToSection } from "../Masthead";

export type PaperState = "loading" | "ready" | "failed" | "setting";

export interface EditionDeskProps {
  /** The reader's current round: the page's stand-in while the paper is being set or when the wire is down. */
  cards: InSnipCard[];
  paper: EditionPaper | null;
  paperState: PaperState;
  /** Every story the reader has opened (this round and before). */
  readIds: string[];
  goodNewsCount: number;
  gamePlayed: boolean;
  gameAvailable: boolean;
  quizAvailable: boolean;
  joined: boolean;
  /** Stories that landed since the page was set. */
  fresh?: number;
  onRefresh?: () => void;
  /** Web push for the morning front page. */
  pushState?: "unknown" | "unsupported" | "off" | "on" | "busy";
  onEnablePush?: () => void;
  /** "Front page first": open on the front page every visit. */
  frontPageFirst?: boolean;
  onToggleFrontPageFirst?: () => void;
  /** Share the front page as a picture; resolves to how it went. */
  onShareImage?: (cards: InSnipCard[], title: string) => Promise<"shared" | "downloaded" | "failed">;
  /** Open a story in the reader, with the next unread stories from this page lined up behind it. */
  onOpenStory: (id: string, chain: string[]) => void;
  /** Read the page aloud from this story on, the chain behind it. */
  onListen: (id: string, chain: string[]) => void;
  onPlayGame: () => void;
  onPlayQuiz: () => void;
  onGoodNews: () => void;
  onJoin: (email: string) => boolean;
  /** Share the page as a paste-anywhere text block (the picture's fallback). */
  onShare: (cards: InSnipCard[], title: string) => void;
  onClose: () => void;
}

const WORDS_PER_MINUTE_ALOUD = 165;
const UNIT_SHORT: Record<string, string> = { k: "k", m: "m", bn: "bn", b: "bn", million: "m", billion: "bn", trillion: "tn" };

/** The figures the day's headlines carry: "4.3%", "$260m", "7,000". A figure counts when it
 *  says something on its own (a percentage, a sum, a count in the thousands, a span of
 *  years); a bare "3" or a year does not. Up to three stories, in page order. */
export function dayFigures<T extends { id: string; headline: string; category?: string }>(stories: T[], max = 3): { id: string; figure: string; headline: string; category: string }[] {
  const re = /(?:A?\$|£|€)?\d{1,3}(?:,\d{3})+(?:\.\d+)?|(?:A?\$|£|€)\d+(?:\.\d+)?\s?(?:k|m|bn|b|million|billion|trillion)?|\d+(?:\.\d+)?\s?(?:%|per cent|pc\b|million|billion|trillion|bn\b|m\b|-year|-day|-hour|km\b|mm\b|°C)/gi;
  const out: { id: string; figure: string; headline: string; category: string }[] = [];
  for (const s of stories) {
    const hits = (s.headline.match(re) ?? []).map((h) => h.trim()).filter((h) => !/^(19|20)\d\d$/.test(h));
    if (!hits.length) continue;
    // The strongest figure in the headline: a sum or a percentage before a plain count.
    hits.sort((a, b) => Number(/[%$£€]|per cent|bn|billion|million/i.test(b)) - Number(/[%$£€]|per cent|bn|billion|million/i.test(a)));
    const figure = hits[0].replace(/\s?per cent/i, "%").replace(/\s+(k|m|bn|b|million|billion|trillion)$/i, (_, u: string) => UNIT_SHORT[u.toLowerCase()] ?? u);
    out.push({ id: s.id, figure, headline: s.headline, category: s.category || "News" });
    if (out.length >= max) break;
  }
  return out;
}
/** Beside the lead: how many stories stand in the rail. */
const RAIL = 5;

export function EditionDesk({
  cards,
  paper,
  paperState,
  readIds,
  goodNewsCount,
  gamePlayed,
  gameAvailable,
  quizAvailable,
  joined,
  fresh = 0,
  onRefresh,
  pushState = "unknown",
  onEnablePush,
  frontPageFirst = false,
  onToggleFrontPageFirst,
  onShareImage,
  onOpenStory,
  onListen,
  onPlayGame,
  onPlayQuiz,
  onGoodNews,
  onJoin,
  onShare,
  onClose,
}: EditionDeskProps) {
  const [email, setEmail] = useState("");
  const [joinState, setJoinState] = useState<"idle" | "invalid" | "done">(joined ? "done" : "idle");
  const [shareState, setShareState] = useState<"idle" | "busy" | "done" | "saved">("idle");
  // Where the view transition API is missing, the page steps aside with a short fade before
  // the story opens; where it exists, the lead's photograph morphs into the reader's plate.
  const [leaving, setLeaving] = useState(false);
  // The clock on the masthead ticks once a minute so "Set 12 minutes ago" stays true.
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The game / Good News overlays stack above the page; their own Escape wins.
      if (document.querySelector(".son-overlay, .goodnews-overlay")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (shareState !== "done" && shareState !== "saved") return;
    const t = window.setTimeout(() => setShareState("idle"), 2400);
    return () => window.clearTimeout(t);
  }, [shareState]);

  // The page's stories: the paper when it is set, the reader's round while it is being set
  // (so the masthead and the wire never wait on the network) or when the wire is down.
  const onPaper = !!paper && paper.cards.length > 0;
  const stories = useMemo(() => (onPaper ? paper!.cards.slice(0, 12) : cards.slice(0, 12)), [onPaper, paper, cards]);
  const marks = paper?.marks ?? {};
  const read = useMemo(() => new Set(readIds), [readIds]);
  const slot = editionSlot(new Date(nowMs));
  const stats = useMemo(() => paperStats(stories), [stories]);
  const audioMinutes = Math.max(1, Math.round(
    stories.reduce((w, c) => w + `${c.headline} ${c.essence} ${c.body ?? ""}`.split(/\s+/).length, 0) / WORDS_PER_MINUTE_ALOUD,
  ));

  if (!stories.length) return null;

  const lead = stories[0];
  const rail = stories.slice(1, 1 + RAIL);
  const below = stories.slice(1 + RAIL);
  const readCount = stories.filter((c) => read.has(c.id)).length;
  const figures = dayFigures(stories);
  const nextCard = stories.find((c) => !read.has(c.id)) ?? null;
  const dateLine = new Date(nowMs).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const setAt = onPaper ? setAtLabel(paper!.at, nowMs) : "";
  const hours = onPaper ? paper!.hours : 24;

  // The next unread stories from this page, in page order, line up behind an opened story so
  // the reader can keep turning the page from inside the reader. A story off the page (from
  // the wire) takes the page from the top.
  const chainFrom = (id: string) => {
    const i = stories.findIndex((c) => c.id === id);
    return stories.slice(Math.max(0, i + 1)).filter((c) => !read.has(c.id) && c.id !== id).slice(0, 5).map((c) => c.id);
  };

  const open = (id: string) => {
    const hasVt = typeof document !== "undefined" && "startViewTransition" in document;
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const chain = chainFrom(id);
    if (hasVt || reduce || leaving) { onOpenStory(id, chain); return; }
    setLeaving(true);
    window.setTimeout(() => onOpenStory(id, chain), 190);
  };

  const share = async () => {
    if (!onShareImage) { onShare(stories, slot.name); return; }
    if (shareState === "busy") return;
    setShareState("busy");
    const how = await onShareImage(stories, slot.name);
    if (how === "failed") { onShare(stories, slot.name); setShareState("idle"); return; }
    setShareState(how === "shared" ? "done" : "saved");
  };

  // The wire: what landed most recently, on the page or not; the page's own newest when the
  // paper carries no wire of its own.
  const wire = (paper?.wire?.length ? paper.wire : [...stories]
    .filter((c) => c.publishedAt)
    .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt))
    .map((c) => ({ id: c.id, headline: c.headline, at: Date.parse(c.publishedAt) / 1000 }))
  ).slice(0, 8);

  // The rail is what the country is reading when enough readers have been through; otherwise
  // the five the desk ranked next, which is a judgement of the story rather than of the readers.
  const hotIds = paper?.hot ?? [];
  const railIsHot = hotIds.length >= 3;
  const railLabel = railIsHot ? "Most read right now" : "Also trending";
  const railMax = Math.max(1, ...rail.map((c) => marks[c.id]?.readers ?? 0));
  const heatOf = (c: InSnipCard, i: number) =>
    railIsHot ? Math.max(0.12, (marks[c.id]?.readers ?? 0) / railMax) : Math.max(0.14, (rail.length - i) / rail.length);

  // "Just in" only where it says something: when most of the page landed in the last couple
  // of hours the mark is noise, so it stays on the three newest alone.
  const justIn = new Set(
    stories.filter((c) => marks[c.id]?.justIn).sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt)).slice(0, 3).map((c) => c.id),
  );

  const mark = (card: InSnipCard) => {
    const m = marks[card.id];
    if (!m) return null;
    if (m.breaking) return <em className="pp-mark breaking">Breaking</em>;
    if (m.hot) return <em className="pp-mark hot"><Flame size={10} aria-hidden="true" /> Most read</em>;
    if (justIn.has(card.id)) return <em className="pp-mark justin"><i aria-hidden="true" /> Just in</em>;
    return null;
  };

  const kicker = (card: InSnipCard) => (
    <span className="ed-kicker pp-kicker">
      <span className="ed-kicker-cat">{card.category || "News"}</span>
      {card.publishedAt ? <span className="pp-when">{agoShort(card.publishedAt, nowMs)}</span> : null}
      {mark(card)}
    </span>
  );

  const readMark = (card: InSnipCard) =>
    read.has(card.id) ? (
      <span className="ed-read-mark">
        <Check size={11} aria-hidden="true" /> Read
      </span>
    ) : null;

  // The six below the fold: a picture each where there is one, and only the headline under
  // it. The essence waits for the reader to open the story.
  const plateCell = (card: InSnipCard, n: number) => (
    <button key={card.id} type="button" className={`ed-plate pp-plate${read.has(card.id) ? " read" : ""}${card.imageUrl ? "" : " nopic"}`} onClick={() => open(card.id)}>
      {card.imageUrl ? (
        <span className="ed-plate-pic" aria-hidden="true">
          <img src={resolveImageUrl(card.imageUrl)} alt="" loading="lazy" decoding="async" />
        </span>
      ) : null}
      <span className="pp-plate-copy">
        {kicker(card)}
        <strong className="ed-plate-head"><i className="pp-n" aria-hidden="true">{String(n).padStart(2, "0")}</i>{card.headline}</strong>
        {!card.imageUrl ? <span className="ed-cell-essence">{card.essence}</span> : null}
        <span className="pp-cell-foot">
          {sourceName(card.primarySourceName, card.primarySourceUrl) ? <span className="pp-source">{sourceName(card.primarySourceName, card.primarySourceUrl)}</span> : null}
          {readMark(card)}
        </span>
      </span>
    </button>
  );

  // The corner: the paper's own pieces, one row, only what is on today.
  const corner: React.ReactNode[] = [];
  if (gameAvailable) {
    corner.push(
      <button key="ed-game" type="button" className="pp-door game" onClick={onPlayGame}>
        <span className="pp-door-ico" aria-hidden="true"><Play size={15} /></span>
        <span className="pp-door-copy">
          <strong>Signal or Noise</strong>
          <span>{gamePlayed ? "Solved for today. See your calls and score." : "Six headlines. Call real signal or noise."}</span>
        </span>
        <ArrowRight size={14} aria-hidden="true" />
      </button>,
    );
  }
  if (quizAvailable) {
    corner.push(
      <button key="ed-quiz" type="button" className="pp-door quiz" onClick={onPlayQuiz}>
        <span className="pp-door-ico" aria-hidden="true"><Check size={15} /></span>
        <span className="pp-door-copy">
          <strong>The front page quiz</strong>
          <span>Five facts from today's stories. Match each to its story.</span>
        </span>
        <ArrowRight size={14} aria-hidden="true" />
      </button>,
    );
  }
  if (goodNewsCount > 0) {
    corner.push(
      <button key="ed-sun" type="button" className="pp-door sun" onClick={onGoodNews}>
        <span className="pp-door-ico" aria-hidden="true"><Sun size={15} /></span>
        <span className="pp-door-copy">
          <strong>The bright side</strong>
          <span>{goodNewsCount === 1 ? "One genuinely good story today." : `${goodNewsCount} genuinely good stories today.`}</span>
        </span>
        <ArrowRight size={14} aria-hidden="true" />
      </button>,
    );
  }

  const submitJoin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onJoin(email)) setJoinState("done");
    else setJoinState("invalid");
  };

  const shareWord =
    shareState === "busy" ? "Making the picture" : shareState === "done" ? "Shared" : shareState === "saved" ? "Saved to your pictures" : "Share the front page";
  const arc = dayArc(new Date(nowMs));
  // The sun's place on the arc: a half circle from the left horizon (midnight) over the top
  // (noon) to the right horizon. Night hours sit below the horizon line.
  const theta = Math.PI * (1 - arc);
  const R = 84;
  // The arc runs midnight (left horizon) over noon to midnight (right); the sun is up for the
  // middle half of it, six to six, and sits just under the horizon line through the night.
  const up = arc >= 0.25 && arc <= 0.75;
  const sunX = 120 + R * Math.cos(theta);
  const sunY = up ? 90 - R * Math.sin(theta) : 90 + 9;
  // The part of the day already gone, from the left horizon (midnight) to the sun.
  // A half circle in one arc command is ambiguous, so the whole day gone is drawn in two.
  const goneTo = up ? `${sunX.toFixed(1)} ${sunY.toFixed(1)}` : arc > 0.5 ? `120 ${90 - R} A${R} ${R} 0 0 1 ${120 + R} 90` : "";
  const tick = (f: number) => ({ x: (120 + R * Math.cos(Math.PI * (1 - f))).toFixed(1), y: (90 - R * Math.sin(Math.PI * (1 - f))).toFixed(1) });

  return (
    <section className={`edition-desk pp slot-${slot.key}${leaving ? " ed-leaving" : ""}`} role="dialog" aria-modal="true" aria-label={slot.name}>
      {/* THE MASTHEAD: the sheet's own shelf (the name, the pillars with Edition inked, the set-at
          time) and the parts of this page as sections that follow the scroll. Part of the sheet,
          above the page that scrolls. */}
      <div className="ed-bars">
        <div className="site-shelf">
          <a className="ed-shelf-brand" href="/" aria-label="InSnip home">In<b>Snip</b></a>
          <Pillars
            active="edition"
            onPillar={(id) => {
              if (id === "edition") { scrollToSection("pp-mast"); return true; }
              if (id === "news") { onClose(); return true; }
              return false;
            }}
          />
          <span className="ed-shelf-set">{paperState === "setting" || paperState === "loading" ? "Setting the page" : setAt || ""}</span>
        </div>
        <Sections
          spy
          label="On this page"
          sections={[
            { id: "pp-front", label: "Front page", href: "#pp-front" },
            { id: "pp-numbers", label: "In numbers", href: "#pp-numbers" },
            { id: "pp-more", label: "More from the day", href: "#pp-more" },
          ]}
        />
      </div>
      <div className="ed-scroll">
        <header className="ed-masthead pp-mast" id="pp-mast">
          <div className="ed-topline">
            <span className="ed-date">{dateLine}<i className="pp-ledger-in">{`, ${stats.stories} stories, about ${stats.minutes} min`}</i></span>
            <span className="ed-brand" aria-hidden="true">In<b>Snip</b></span>
            <span className="ed-issue pp-set">
              {paperState === "setting" || paperState === "loading" ? "Setting the page" : setAt || "Today's round"}
            </span>
          </div>
          <div className="pp-arc" aria-hidden="true">
            <svg viewBox="0 0 240 114" width="222" height="105">
              <path d={`M${120 - R} 90 A${R} ${R} 0 0 1 ${120 + R} 90`} className="pp-arc-path" />
              <line x1="12" y1="90" x2="228" y2="90" className="pp-arc-horizon" />
              {[0.25, 0.5, 0.75].map((f) => { const t = tick(f); return <circle key={f} cx={t.x} cy={t.y} r="1.6" className="pp-arc-tick" />; })}
              <text x={tick(0.25).x} y="106" className="pp-arc-hour">6 am</text>
              <text x="120" y="1" className="pp-arc-hour">noon</text>
              <text x={tick(0.75).x} y="106" className="pp-arc-hour">6 pm</text>
              {goneTo ? <path d={`M${120 - R} 90 A${R} ${R} 0 0 1 ${goneTo}`} className="pp-arc-gone" /> : null}
              {up ? <circle cx={sunX.toFixed(1)} cy={sunY.toFixed(1)} r="7" className="pp-arc-halo" /> : null}
              {/* After dark the sun is not on the horizon: a small moon under the line says night. */}
              {up ? <circle cx={sunX.toFixed(1)} cy={sunY.toFixed(1)} r="5" className={`pp-arc-sun ${slot.key}`} /> : <path d="M116 104 a5 5 0 1 0 6 6 a4 4 0 0 1 -6 -6 z" transform="translate(1 -8)" className="pp-arc-moon" />}
            </svg>
          </div>
          <h1 className="ed-title">{slot.name}</h1>
          <p className="pp-line">{slot.line}</p>

          <div className="ed-mast-acts">
            <button type="button" className="ed-listen" onClick={() => onListen((nextCard ?? lead).id, chainFrom((nextCard ?? lead).id))}>
              <Play size={11} aria-hidden="true" />
              Listen to this edition
              <i>about {audioMinutes} min</i>
            </button>
            <button type="button" className={`ed-listen ed-share${shareState === "done" || shareState === "saved" ? " did" : ""}`} onClick={() => void share()} disabled={shareState === "busy"} title="A picture of this front page, ready to post">
              {shareState === "busy" ? <ImageDown size={11} aria-hidden="true" /> : shareState === "idle" ? <Share2 size={11} aria-hidden="true" /> : <Check size={11} aria-hidden="true" />}
              {shareWord}
            </button>
          </div>
        </header>

        {fresh > 0 && onRefresh ? (
          <button type="button" className="ed-fresh" onClick={onRefresh} disabled={paperState === "setting"}>
            <RefreshCw size={12} aria-hidden="true" className={paperState === "setting" ? "pp-spin" : undefined} />
            <span>{fresh === 1 ? "A new story landed since this page was set." : `${fresh} new stories landed since this page was set.`}</span>
            <b>{paperState === "setting" ? "Setting the page" : "Set the page afresh"}</b>
          </button>
        ) : null}

        {wire.length >= 3 ? (
          <div className="pp-wire" id="pp-wire" aria-label="Just landed">
            <span className="pp-wire-label"><i aria-hidden="true" />On the wire</span>
            <div className="pp-wire-track">
              <div className="pp-wire-run">
                {[0, 1].map((copy) => (
                  <span key={copy} className="pp-wire-set" aria-hidden={copy === 1}>
                    {wire.map((c) => (
                      <button key={`${copy}-${c.id}`} type="button" className="pp-wire-item" onClick={() => open(c.id)} tabIndex={copy === 1 ? -1 : 0}>
                        <b>{agoShort(c.at, nowMs)}</b>
                        <span>{c.headline}</span>
                      </button>
                    ))}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {paperState === "loading" && !onPaper ? (
          <div className="pp-setting" aria-busy="true" aria-label="Setting the page">
            <div className="pp-sk lead" />
            <div className="pp-sk-row"><i /><i /><i /></div>
          </div>
        ) : (
          <>
            <div className="pp-top" id="pp-front">
              <button type="button" className="ed-lead pp-lead" onClick={() => open(lead.id)}>
                <span className={`pp-cover${lead.imageUrl ? "" : " nopic"}`}>
                  {lead.imageUrl ? (
                    <img
                      src={resolveImageUrl(lead.imageUrl)}
                      alt=""
                      loading="eager"
                      decoding="async"
                      style={{ viewTransitionName: "card-media" } as React.CSSProperties}
                    />
                  ) : null}
                  {lead.imageUrl && displayCaption(lead.imageAlt) ? (
                    <span className="pp-cover-caption ed-lead-caption" aria-hidden="true">{displayCaption(lead.imageAlt)}</span>
                  ) : null}
                  <span className="pp-cover-copy ed-lead-copy">
                    <span className="ed-kicker pp-kicker">
                      <span className="ed-kicker-cat">{lead.category || "News"}</span>
                      {lead.publishedAt ? <span className="pp-when">{agoShort(lead.publishedAt, nowMs)}</span> : null}
                      {mark(lead)}
                    </span>
                    <strong className="ed-lead-head">{lead.headline}</strong>
                    <em className="ed-lead-essence">{lead.essence}</em>
                  </span>
                </span>
                <span className="pp-lead-under">
                  {lead.whyItMatters ? <span className="pp-lead-why"><b>Why it matters</b> {lead.whyItMatters}</span> : null}
                  <span className="ed-lead-meta">
                    {read.has(lead.id) ? "Read, " : ""}
                    {sourceName(lead.primarySourceName, lead.primarySourceUrl) || "Source-checked"}
                    {lead.supportingSources?.length ? ` and ${lead.supportingSources.length} more` : ""}
                    <span className="ed-lead-go" aria-hidden="true">Read the story <ArrowRight size={12} /></span>
                  </span>
                </span>
              </button>

              <aside className="pp-rail">
                <h2 className="pp-rail-head">
                  {railIsHot ? <Flame size={12} aria-hidden="true" /> : null}
                  <span>{railLabel}</span>
                </h2>
                <ol className="pp-rank">
                  {rail.map((c, i) => {
                    const w = heatOf(c, i);
                    return (
                      <li key={c.id}>
                        <button type="button" className={read.has(c.id) ? "read" : undefined} onClick={() => open(c.id)}>
                          <i aria-hidden="true">{i + 2}</i>
                          <span className="pp-rank-copy">
                            <strong>{c.headline}</strong>
                            <em>{c.category || "News"}{c.publishedAt ? `, ${agoShort(c.publishedAt, nowMs)}` : ""}{read.has(c.id) ? ", read" : ""}</em>
                            <b className="pp-heat" aria-hidden="true"><u style={{ width: `${Math.round(w * 100)}%` }} /></b>
                          </span>
                          {mark(c)}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </aside>
            </div>

            {figures.length >= 2 ? (
              <section className="pp-numbers" id="pp-numbers" aria-label="The day in numbers">
                <h2 className="pp-section-head"><span>The day in numbers</span><i aria-hidden="true" /></h2>
                <div className={`pp-num-row n${figures.length}`}>
                  {figures.map((f) => (
                    <button key={f.id} type="button" className="pp-num" onClick={() => open(f.id)}>
                      <b>{f.figure}</b>
                      <span>{f.headline}</span>
                      <em>{f.category}</em>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {below.length ? (
              <section className="pp-below" id="pp-more" aria-label="More from the day">
                <h2 className="pp-section-head"><span>More from the day</span><i aria-hidden="true" /></h2>
                <div className="pp-grid">
                  {below.map((c, i) => plateCell(c, i + 2 + RAIL))}
                </div>
              </section>
            ) : null}

            {corner.length ? <div className={`pp-corner n${corner.length}`} aria-label="The corner">{corner}</div> : null}
          </>
        )}

        <footer className="ed-foot pp-foot">
          {/* The page ends by naming the next one: finite by design, with a time to come back. */}
          <p className="ed-next-edition">{slot.key === "late" || slot.key === "evening" ? "That is the day. The Morning Edition is set from 4 am." : slot.key === "morning" ? "The Midday Edition is set from 11 am." : slot.key === "midday" ? "The Afternoon Edition is set from 2 pm." : "The Evening Edition is set from 5 pm."}</p>
          {joinState === "done" ? (
            <p className="ed-join-done">
              <Check size={13} aria-hidden="true" /> You're in. Tomorrow's edition lands in your inbox.
            </p>
          ) : (
            <form className="ed-join" onSubmit={submitJoin}>
              <span className="ed-join-copy">One calm email a day. No noise, no doomscroll.</span>
              <span className="ed-join-row">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  aria-label="Email address"
                  aria-invalid={joinState === "invalid"}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (joinState === "invalid") setJoinState("idle"); }}
                />
                <button type="submit">Join</button>
              </span>
              {joinState === "invalid" ? <small className="ed-join-error">Enter a valid email.</small> : null}
            </form>
          )}
          <div className="pp-prefs">
            {onToggleFrontPageFirst ? (
              <button type="button" className={`ed-listen ed-pinit${frontPageFirst ? " on" : ""}`} onClick={onToggleFrontPageFirst} aria-pressed={frontPageFirst} title={frontPageFirst ? "InSnip opens on the front page. Press to open on the stories instead." : "Open InSnip on the front page every time"}>
                <Pin size={11} aria-hidden="true" />
                {frontPageFirst ? "Front page first" : "Start here each day"}
              </button>
            ) : null}
            {onEnablePush && pushState !== "unsupported" && pushState !== "unknown" ? (
              <button type="button" className={`ed-listen ed-bell${pushState === "on" ? " on" : ""}`} onClick={pushState === "off" ? onEnablePush : undefined} disabled={pushState !== "off"} aria-pressed={pushState === "on"} title={pushState === "on" ? "The front page comes to you each morning" : "A nudge each morning when the front page is set"}>
                {pushState === "on" ? <BellRing size={11} aria-hidden="true" /> : <Bell size={11} aria-hidden="true" />}
                {pushState === "on" ? "Morning front page on" : pushState === "busy" ? "Turning on" : "The front page each morning"}
              </button>
            ) : null}
            <p className="ed-colophon">
              {onPaper
                ? `${setAt} from the last ${hours} hours. Set again as stories land; nothing moves while you read.`
                : "Curated, source-checked and set fresh every round."}
            </p>
          </div>
        </footer>
      </div>

      <div className="ed-bar">
        <span className="ed-bar-note" aria-hidden="true">
          {readCount >= stories.length ? "Every story on this page read." : `${readCount} of ${stories.length} read`}
        </span>
        <button
          type="button"
          className={`ed-begin${nextCard ? " ed-begin-next" : ""}`}
          onClick={() => (nextCard ? open(nextCard.id) : onClose())}
        >
          <span className="ed-begin-copy">
            <em>{!nextCard ? "Back to the reader" : readCount > 0 ? "Next unread" : "Start reading"}</em>
            {nextCard ? <strong>{nextCard.headline}</strong> : null}
          </span>
          <ArrowRight size={16} aria-hidden="true" />
        </button>
      </div>
      <button type="button" className="ed-close" onClick={onClose} aria-label="Close the front page">
        <X size={17} aria-hidden="true" />
      </button>
    </section>
  );
}
